import React, { useState } from 'react';
import { Client, BannerSet, DataFeed } from '../types';
import { ArrowLeft, Plus, Search, Upload, Calendar, Activity, Eye, Settings, Trash2 } from 'lucide-react';
import { BannerSetSettingsModal } from './BannerSetSettingsModal';
import { AddBannerSetModal } from './AddBannerSetModal';
import { ConfirmDialog } from './ConfirmDialog';
import { useToast } from '../contexts/ToastContext';

interface ClientDashboardProps {
  client: Client;
  onNavigateToBannerSet: (clientId: string, bannerSetId: string) => void;
  onUpdateClient: (updates: Partial<Client>) => void;
  onBack: () => void;
}

export const ClientDashboard: React.FC<ClientDashboardProps> = ({
  client,
  onNavigateToBannerSet,
  onUpdateClient,
  onBack
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'draft' | 'archived'>('all');
  const [settingsModal, setSettingsModal] = useState<{ isOpen: boolean; bannerSetId: string | null }>({
    isOpen: false,
    bannerSetId: null
  });
  const [showAddBannerSetModal, setShowAddBannerSetModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; bannerSetId: string | null; bannerSetName: string }>({
    isOpen: false,
    bannerSetId: null,
    bannerSetName: ''
  });

  const { showSuccess, showError, showWarning } = useToast();

  const filteredBannerSets = client.bannerSets.filter(set => {
    const matchesSearch = set.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         set.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || set.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: BannerSet['status']) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getCompletedSizes = (bannerSet: BannerSet) => {
    return bannerSet.sizes.filter(size => size.elements.length > 0).length;
  };

  const handleOpenSettings = (bannerSetId: string) => {
    setSettingsModal({ isOpen: true, bannerSetId });
  };

  const handleCloseSettings = () => {
    setSettingsModal({ isOpen: false, bannerSetId: null });
  };

  const handleUpdateBannerSet = (bannerSetId: string, updates: Partial<BannerSet>) => {
    const updatedBannerSets = client.bannerSets.map(set => 
      set.id === bannerSetId ? { ...set, ...updates } : set
    );
    onUpdateClient({ bannerSets: updatedBannerSets });
  };

  const handleAddBannerSet = (newBannerSet: BannerSet) => {
    const updatedBannerSets = [...client.bannerSets, newBannerSet];
    onUpdateClient({ bannerSets: updatedBannerSets });
  };

  const handleDeleteBannerSet = (bannerSetId: string) => {
    const bannerSet = client.bannerSets.find(set => set.id === bannerSetId);
    if (!bannerSet) return;

    // Check if banner set has completed banners
    const completedSizes = getCompletedSizes(bannerSet);
    if (completedSizes > 0) {
      showError(
        'Cannot Delete Banner Set', 
        `"${bannerSet.name}" has ${completedSizes} completed banner${completedSizes !== 1 ? 's' : ''}. Please clear all banners first or archive the set instead.`
      );
      return;
    }

    setDeleteConfirm({
      isOpen: true,
      bannerSetId,
      bannerSetName: bannerSet.name
    });
  };

  const confirmDeleteBannerSet = () => {
    if (deleteConfirm.bannerSetId) {
      const updatedBannerSets = client.bannerSets.filter(set => set.id !== deleteConfirm.bannerSetId);
      onUpdateClient({ bannerSets: updatedBannerSets });
      showWarning('Banner Set Deleted', `"${deleteConfirm.bannerSetName}" has been permanently deleted`);
      setDeleteConfirm({ isOpen: false, bannerSetId: null, bannerSetName: '' });
    }
  };

  const currentBannerSet = settingsModal.bannerSetId 
    ? client.bannerSets.find(set => set.id === settingsModal.bannerSetId)
    : null;

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{client.name}</h1>
                <p className="text-gray-600 mt-1">Manage banner sets and campaigns</p>
              </div>
            </div>
            <button 
              onClick={() => setShowAddBannerSetModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={20} />
              <span>New Banner Set</span>
            </button>
          </div>

          {/* Client Info */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm font-medium text-gray-600">Email</p>
                <p className="text-lg text-gray-900">{client.email}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Role</p>
                <p className="text-lg text-gray-900 capitalize">{client.role}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Last Login</p>
                <p className="text-lg text-gray-900">{formatDate(client.lastLogin)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Status</p>
                <span className={`inline-flex px-2 py-1 text-sm font-medium rounded-full ${
                  client.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {client.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Settings className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Banner Sets</p>
                  <p className="text-2xl font-bold text-gray-900">{client.bannerSets.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Activity className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active Sets</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {client.bannerSets.filter(set => set.status === 'active').length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Calendar className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Draft Sets</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {client.bannerSets.filter(set => set.status === 'draft').length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Eye className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Sizes</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {client.bannerSets.reduce((total, set) => total + set.sizes.length, 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filter */}
          <div className="flex items-center space-x-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search banner sets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        {/* Banner Sets Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBannerSets.map(bannerSet => (
            <div
              key={bannerSet.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{bannerSet.name}</h3>
                    <p className="text-sm text-gray-600">{bannerSet.description}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(bannerSet.status)}`}>
                      {bannerSet.status}
                    </span>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleOpenSettings(bannerSet.id)}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        title="Banner Set Settings"
                      >
                        <Settings size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteBannerSet(bannerSet.id)}
                        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete Banner Set"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Sizes Completed:</span>
                    <span className="font-medium text-gray-900">
                      {getCompletedSizes(bannerSet)} / {bannerSet.sizes.length}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Data Items:</span>
                    <span className="font-medium text-gray-900">
                      {bannerSet.dataFeedArray.length || 'None'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Last Modified:</span>
                    <span className="font-medium text-gray-900">{formatDate(bannerSet.lastModified)}</span>
                  </div>
                </div>

                {/* Data Feed Status */}
                {bannerSet.dataFeedArray.length > 0 ? (
                  <div className="mb-4 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                    ✓ Data feed connected ({bannerSet.dataFeedArray.length} items)
                  </div>
                ) : (
                  <div className="mb-4 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                    ⚠ No data feed uploaded
                  </div>
                )}

                {/* Actions */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => onNavigateToBannerSet(client.id, bannerSet.id)}
                    className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                  >
                    Edit Banner Set
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredBannerSets.length === 0 && (
          <div className="text-center py-12">
            <Settings className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No banner sets found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search terms.' : 'Get started by creating your first banner set.'}
            </p>
          </div>
        )}
      </div>

      {/* Settings Modal */}
      {currentBannerSet && (
        <BannerSetSettingsModal
          isOpen={settingsModal.isOpen}
          onClose={handleCloseSettings}
          bannerSet={currentBannerSet}
          onUpdateBannerSet={(updates) => handleUpdateBannerSet(currentBannerSet.id, updates)}
        />
      )}

      {/* Add Banner Set Modal */}
      <AddBannerSetModal
        isOpen={showAddBannerSetModal}
        onClose={() => setShowAddBannerSetModal(false)}
        onAddBannerSet={handleAddBannerSet}
        clientName={client.name}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Banner Set"
        message={`Are you sure you want to permanently delete "${deleteConfirm.bannerSetName}"? This action cannot be undone.`}
        confirmText="Delete Banner Set"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDeleteBannerSet}
        onCancel={() => setDeleteConfirm({ isOpen: false, bannerSetId: null, bannerSetName: '' })}
      />
    </div>
  );
};